/* InkBoard ― キャンバス本体
 * 試作（prototypes/inkboard.html）から中身をそのまま移したもの。
 * 同期（Yjs）は sync.js 側から、この中の関数を呼び出して繋ぎ込む。
 */
"use strict";


(() => {   // iPad Safari がページ自体をピンチしないように
  let m = document.querySelector('meta[name="viewport"]');
  if (!m) { m = document.createElement("meta"); m.name = "viewport"; document.head.appendChild(m); }
  m.content = "width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover";
})();

const $ = (id) => document.getElementById(id);
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const uid = () => Math.random().toString(36).slice(2, 10);

/* ============================================================
   core ― ビューポートとストローク（描画にもカードにも共通の土台）
   ============================================================ */
const viewport = {
  x: 0, y: 0, zoom: 1,
  toWorld(px, py) { return { x: (px - this.x) / this.zoom, y: (py - this.y) / this.zoom }; },
  zoomAt(px, py, factor) {
    const next = clamp(this.zoom * factor, 0.1, 8);
    const k = next / this.zoom;
    this.x = px - (px - this.x) * k;
    this.y = py - (py - this.y) * k;
    this.zoom = next;
  }
};

const STRIDE = 6;
class StrokeBuf {
  constructor(cap = 128) { this.a = new Float32Array(cap * STRIDE); this.n = 0; }
  push(x, y, p, tx, ty, t) {
    if ((this.n + 1) * STRIDE > this.a.length) {
      const b = new Float32Array(this.a.length * 2); b.set(this.a); this.a = b;
    }
    const o = this.n * STRIDE;
    this.a[o]=x; this.a[o+1]=y; this.a[o+2]=p; this.a[o+3]=tx; this.a[o+4]=ty; this.a[o+5]=t;
    this.n++;
  }
  x(i){return this.a[i*STRIDE];}   y(i){return this.a[i*STRIDE+1];}
  p(i){return this.a[i*STRIDE+2];} tx(i){return this.a[i*STRIDE+3];} ty(i){return this.a[i*STRIDE+4];}
}

function recalcBBox(s) {
  let x0=Infinity,y0=Infinity,x1=-Infinity,y1=-Infinity;
  for (let i=0;i<s.buf.n;i++){
    const x=s.buf.x(i), y=s.buf.y(i);
    if(x<x0)x0=x; if(y<y0)y0=y; if(x>x1)x1=x; if(y>y1)y1=y;
  }
  const pad = s.width*2+4;
  s.bbox = [x0-pad, y0-pad, x1+pad, y1+pad];
  return s;
}

const ink = {
  strokes: [],
  add(s) {
    if (!s.id) s.id = uid();
    recalcBBox(s);
    this.strokes.push(s);
  }
};

/* ============================================================
   画像置き場 ― 実体はボードの外に置き、中身の指紋で参照する
   ボードに画像を埋め込むと、線を1本足すたびに画像ごと同期してしまう。
   ============================================================ */
const assetURLs = new Map();      // 指紋 → 表示用のURL
const assetPending = new Map();   // 取り寄せ中のもの

const b64enc = (u8) => {
  let out = "";
  const chunk = 0x8000;
  for (let i = 0; i < u8.length; i += chunk) {
    out += String.fromCharCode.apply(null, u8.subarray(i, i + chunk));
  }
  return btoa(out);
};
const b64dec = (str) => {
  const bin = atob(str);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return u8;
};

/** 中身から指紋を作る。同じ画像を何枚貼っても実体はひとつで済む */
async function hashBytes(bytes) {
  const d = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, "0")).join("").slice(0, 40);
}

async function readAssetLocal(hash) {
  const v = await storage.get("asset:" + hash);
  if (!v) return null;
  if (typeof v === "string") return b64dec(v);
  return v instanceof Uint8Array ? v : new Uint8Array(v);
}

async function writeAssetLocal(hash, bytes) {
  // IndexedDB はそのまま入るが、それ以外は文字にしてから入れる
  await storage.set("asset:" + hash, storage.kind === "indexeddb" ? bytes : b64enc(bytes));
}

/** 手元に無ければ他の端末から取り寄せる */
async function assetURL(hash, mime) {
  if (!hash) return null;
  if (assetURLs.has(hash)) return assetURLs.get(hash);
  if (assetPending.has(hash)) return assetPending.get(hash);

  const job = (async () => {
    let bytes = await readAssetLocal(hash);
    if (!bytes && hooks.fetchAsset) {
      bytes = await hooks.fetchAsset(hash);
      if (bytes) await writeAssetLocal(hash, bytes);
    }
    if (!bytes) return null;
    const url = URL.createObjectURL(new Blob([bytes], { type: mime || "image/png" }));
    assetURLs.set(hash, url);
    return url;
  })();
  assetPending.set(hash, job);
  const url = await job;
  assetPending.delete(hash);
  return url;
}

async function saveAsset(bytes, mime) {
  const hash = await hashBytes(bytes);
  if (!(await readAssetLocal(hash))) await writeAssetLocal(hash, bytes);
  assetURLs.delete(hash);
  if (hooks.putAsset) { try { await hooks.putAsset(hash, bytes, mime); } catch (e) { console.error(e); } }
  return hash;
}

/* ============================================================
   元に戻す ― 逆操作を覚えておく方式
   丸ごとの控えを取ると、線が数千本あるボードでは重すぎる。
   「何をしたか」と「その戻し方」だけを持つ。
   ============================================================ */
const history = { past: [], future: [], limit: 120 };
let applyingHistory = false;

function pushHistory(entry) {
  if (loading || applyingHistory) return;
  history.past.push(entry);
  if (history.past.length > history.limit) history.past.shift();
  history.future.length = 0;     // 新しい操作をしたら、やり直せる先は消える
  paintHistory();
}
function undo() {
  const e = history.past.pop();
  if (!e) return;
  applyingHistory = true;
  try { e.undo(); } finally { applyingHistory = false; }
  history.future.push(e);
  paintHistory();
  markDirty();
}
function redo() {
  const e = history.future.pop();
  if (!e) return;
  applyingHistory = true;
  try { e.redo(); } finally { applyingHistory = false; }
  history.past.push(e);
  paintHistory();
  markDirty();
}
function clearHistory() {
  history.past.length = 0;
  history.future.length = 0;
  paintHistory();
}
function paintHistory() {
  const u = document.getElementById("btnUndo"), r = document.getElementById("btnRedo");
  if (u) u.disabled = history.past.length === 0;
  if (r) r.disabled = history.future.length === 0;
}

const cloneProps = (p) => JSON.parse(JSON.stringify(p));
const cardState = (c) => ({ props: cloneProps(c.props), x: c.x, y: c.y, w: c.w, h: c.h });

/* ============================================================
   cards ― カードの種類はここに「登録」するだけで増える
   ============================================================ */
const registry = new Map();
function registerCard(plugin) { registry.set(plugin.type, plugin); }
const cards = [];               // 表示順（後ろほど手前）

/** テキストカード：contenteditable なので日本語IMEがそのまま効く */
registerCard({
  type: "text",
  name: "テキスト",
  defaultProps: () => ({ text: "" }),
  defaultSize: () => ({ w: 1350, h: 840 }),
  mount(el, card) {
    const body = document.createElement("div");
    body.className = "body";
    body.contentEditable = "true";
    body.spellcheck = false;
    body.dataset.ph = "ここに入力";
    body.textContent = card.props.text;
    body.addEventListener("focus", () => { card.__before = cardState(card); });
    body.addEventListener("blur", () => {
      card.props.text = body.innerText;
      fitCardHeight(card);      // はみ出したぶんだけ伸ばす
      if (card.__before && JSON.stringify(card.__before) !== JSON.stringify(cardState(card))) {
        pushCardChange(card, card.__before, cardState(card));
      }
      card.__before = null;
      markDirty();
    });
    body.addEventListener("pointerdown", (e) => { if (editing === card) e.stopPropagation(); });
    el.appendChild(body);
    el._edit = () => { body.focus(); placeCaretEnd(body); };
    el._blur = () => body.blur();
  },
  toSearchText: (p) => p.text
});

/** ToDoカード：行の追加・チェック・打ち消し */
registerCard({
  type: "todo",
  name: "ToDo",
  defaultProps: () => ({ items: [{ text: "", done: false }] }),
  defaultSize: () => ({ w: 1350, h: 900 }),
  mount(el, card) {
    const body = document.createElement("div");
    body.className = "body";
    el.appendChild(body);

    const draw = () => {
      body.innerHTML = "";
      card.props.items.forEach((item, i) => {
        const row = document.createElement("div");
        row.className = "item" + (item.done ? " done" : "");
        const box = document.createElement("div");
        box.className = "box";
        box.textContent = item.done ? "✓" : "";
        box.addEventListener("pointerdown", (e) => {
          e.stopPropagation();
          const before = cardState(card);
          item.done = !item.done;
          draw();
          pushCardChange(card, before, cardState(card));
          markDirty();
        });
        const txt = document.createElement("div");
        txt.className = "txt";
        txt.contentEditable = "true";
        txt.spellcheck = false;
        txt.textContent = item.text;
        txt.addEventListener("focus", () => { if (!card.__before) card.__before = cardState(card); });
        txt.addEventListener("blur", () => {
          item.text = txt.innerText;
          fitCardHeight(card);
          if (card.__before && JSON.stringify(card.__before) !== JSON.stringify(cardState(card))) {
            pushCardChange(card, card.__before, cardState(card));
          }
          card.__before = null;
          markDirty();
        });
        txt.addEventListener("pointerdown", (e) => { if (editing === card) e.stopPropagation(); });
        txt.addEventListener("keydown", (e) => {
          if (e.key === "Enter" && !e.isComposing) {
            e.preventDefault();
            item.text = txt.innerText;
            card.props.items.splice(i + 1, 0, { text: "", done: false });
            draw();
            fitCardHeight(card);
            const next = body.querySelectorAll(".txt")[i + 1];
            if (next) { next.focus(); }
          }
          if (e.key === "Backspace" && txt.innerText === "" && card.props.items.length > 1) {
            e.preventDefault();
            card.props.items.splice(i, 1);
            draw();
            const prev = body.querySelectorAll(".txt")[Math.max(0, i - 1)];
            if (prev) { prev.focus(); placeCaretEnd(prev); }
          }
        });
        row.append(box, txt);
        body.appendChild(row);
      });
      const add = document.createElement("button");
      add.className = "add";
      add.textContent = "＋ 行を追加";
      add.addEventListener("pointerdown", (e) => {
        e.stopPropagation();
        card.props.items.push({ text: "", done: false });
        draw();
        fitCardHeight(card);
        const rows = body.querySelectorAll(".txt");
        rows[rows.length - 1].focus();
      });
      body.appendChild(add);
    };
    draw();
    el._edit = () => { const t = body.querySelector(".txt"); if (t) { t.focus(); placeCaretEnd(t); } };
    el._blur = () => { const a = document.activeElement; if (a && body.contains(a)) a.blur(); };
  },
  toSearchText: (p) => p.items.map(i => i.text).join(" ")
});

/** 画像カード */
registerCard({
  type: "image",
  name: "画像",
  defaultProps: () => ({ asset: "", mime: "image/png", name: "" }),
  defaultSize: () => ({ w: 930, h: 660 }),
  mount(el, card) {
    const body = document.createElement("div");
    body.className = "body";
    const img = document.createElement("img");
    img.alt = card.props.name || "画像";
    img.draggable = false;
    body.appendChild(img);
    el.appendChild(body);

    if (card.props.asset) {
      assetURL(card.props.asset, card.props.mime).then((u) => {
        if (u) img.src = u;
        else { img.alt = "画像を取り寄せられませんでした"; el.classList.add("c-image-missing"); }
      });
    } else if (card.props.src) {
      // 古い形式（画像をボードに埋め込んでいたもの）。表示しつつ、裏で外へ出す
      img.src = card.props.src;
      moveInlineImageOut(card);
    }
  },
  toSearchText: (p) => p.name || ""
});

/** 昔のボードに埋め込まれている画像を、置き場へ移す */
async function moveInlineImageOut(card) {
  try {
    const src = card.props.src;
    if (!src || !src.startsWith("data:")) return;
    const mime = (src.match(/^data:([^;,]+)/) || [])[1] || "image/png";
    const bytes = b64dec(src.slice(src.indexOf(",") + 1));
    const hash = await saveAsset(bytes, mime);
    card.props = { asset: hash, mime, name: card.props.name || "" };
    markDirty();
  } catch (e) { console.error("画像の移動に失敗:", e); }
}

function placeCaretEnd(el) {
  const r = document.createRange(); r.selectNodeContents(el); r.collapse(false);
  const s = window.getSelection(); s.removeAllRanges(); s.addRange(r);
}

/* ============================================================
   カードの生成・描画・選択
   ============================================================ */
const cardLayer = $("cardLayer");
let selected = null;
let editing = null;

function createCard(type, props = {}, at) {
  const plugin = registry.get(type);
  if (!plugin) throw new Error("未登録のカード種別: " + type);
  const size = plugin.defaultSize();
  const p = Object.assign(plugin.defaultProps(), props);
  const center = at || viewport.toWorld(stage.clientWidth / 2, stage.clientHeight / 2);
  const card = {
    id: uid(), type, props: p,
    x: Math.round(center.x - size.w / 2), y: Math.round(center.y - size.h / 2),
    w: size.w, h: size.h
  };
  cards.push(card);
  mountCard(card);
  select(card);
  updateEmpty();
  const snapshot = { id: card.id, type: card.type, props: cloneProps(card.props),
                     x: card.x, y: card.y, w: card.w, h: card.h };
  pushHistory({
    undo: () => { const c = cards.find((x) => x.id === snapshot.id); if (c) removeCard(c); },
    redo: () => { restoreCard(snapshot); updateEmpty(); }
  });
  markDirty();
  return card;
}

function mountCard(card) {
  const el = document.createElement("div");
  el.className = `card c-${card.type}`;
  el.dataset.id = card.id;

  const del = document.createElement("button");
  del.className = "del"; del.textContent = "✕"; del.title = "削除";
  del.addEventListener("pointerdown", (e) => { e.stopPropagation(); deleteCard(card); });

  // 角＝縦横、右辺＝幅だけ、下辺＝高さだけ。指でも掴めるよう当たり判定を広げてある
  const handle = document.createElement("div");
  handle.className = "handle";
  handle.addEventListener("pointerdown", (e) => startResize(e, card, "both"));

  const edgeR = document.createElement("div");
  edgeR.className = "edgeR";
  edgeR.title = "幅を変える（ダブルタップで内容に合わせる）";
  edgeR.addEventListener("pointerdown", (e) => startResize(e, card, "w"));

  const edgeB = document.createElement("div");
  edgeB.className = "edgeB";
  edgeB.title = "高さを変える（ダブルタップで内容に合わせる）";
  edgeB.addEventListener("pointerdown", (e) => startResize(e, card, "h"));
  edgeB.addEventListener("dblclick", (e) => { e.stopPropagation(); fitCardHeight(card, true); });

  registry.get(card.type).mount(el, card);
  el.append(del, handle, edgeR, edgeB);
  el.addEventListener("pointerdown", (e) => startCardPointer(e, card));
  cardLayer.appendChild(el);
  card.el = el;
  layoutCard(card);
}

/** 中身が変わったカードの見た目を作り直す（選択は保つ） */
function remountCard(card) {
  const wasSel = selected === card;
  card.el.remove();
  mountCard(card);
  if (wasSel) { selected = card; card.el.classList.add("sel"); }
}

/** 履歴から、カードの中身と大きさをまとめて戻す */
function applyCardState(card, st) {
  card.props = cloneProps(st.props);
  card.x = st.x; card.y = st.y; card.w = st.w; card.h = st.h;
  remountCard(card);
}

/** カードの中身が変わったことを履歴に積む */
function pushCardChange(card, before, after) {
  const id = card.id;
  pushHistory({
    undo: () => { const c = cards.find((x) => x.id === id); if (c) applyCardState(c, before); },
    redo: () => { const c = cards.find((x) => x.id === id); if (c) applyCardState(c, after); }
  });
}

function layoutCard(card) {
  const s = card.el.style;
  s.left = card.x + "px"; s.top = card.y + "px";
  s.width = card.w + "px"; s.height = card.h + "px";
}

/** 利用者の操作で消す（履歴に残る） */
function deleteCard(card) {
  const snapshot = { id: card.id, type: card.type, props: cloneProps(card.props),
                     x: card.x, y: card.y, w: card.w, h: card.h };
  pushHistory({
    undo: () => { restoreCard(snapshot); updateEmpty(); },
    redo: () => { const c = cards.find((x) => x.id === snapshot.id); if (c) removeCard(c); }
  });
  removeCard(card);
}

function removeCard(card) {
  const i = cards.indexOf(card);
  if (i >= 0) cards.splice(i, 1);
  card.el.remove();
  if (selected === card) selected = null;
  if (editing === card) editing = null;
  updateEmpty();
  markDirty();
}

function select(card) {
  if (selected && selected !== card) selected.el.classList.remove("sel");
  if (editing && editing !== card) endEdit();
  selected = card;
  if (card) {
    card.el.classList.add("sel");
    // 選ばれたカードを最前面へ（配列の順＝重ね順）
    cards.splice(cards.indexOf(card), 1);
    cards.push(card);
    cardLayer.appendChild(card.el);
  }
}

function beginEdit(card) {
  if (editing === card) return;
  endEdit();
  editing = card;
  if (card.el._edit) card.el._edit();
}
function endEdit() {
  if (editing && editing.el._blur) editing.el._blur();
  editing = null;
}

function updateEmpty() {
  $("empty").style.display = (cards.length === 0 && ink.strokes.length === 0) ? "" : "none";
}

/* ============================================================
   カードのドラッグとリサイズ
   ============================================================ */
let cardDrag = null;

function startCardPointer(e, card) {
  if (tool !== "select") return;          // ペン・消しゴムのときはカードを掴まない
  e.stopPropagation();
  const wasSelected = selected === card;
  select(card);
  if (e.pointerType === "touch") touches.set(e.pointerId, localXY(e));
  cardDrag = {
    card, id: e.pointerId, mode: "move",
    sx: e.clientX, sy: e.clientY,
    ox: card.x, oy: card.y,
    geo0: cardState(card),
    moved: false, wasSelected
  };
  try { card.el.setPointerCapture(e.pointerId); } catch (err) {}
}

function startResize(e, card, axis) {
  if (tool !== "select") return;
  e.stopPropagation();
  select(card);
  if (e.pointerType === "touch") touches.set(e.pointerId, localXY(e));
  cardDrag = {
    card, id: e.pointerId, mode: "resize", axis: axis || "both",
    sx: e.clientX, sy: e.clientY,
    ow: card.w, oh: card.h, geo0: cardState(card), moved: false
  };
  try { e.target.setPointerCapture(e.pointerId); } catch (err) {}
}

/** 中身がはみ出していたら、収まるところまで高さを伸ばす */
function fitCardHeight(card, shrink) {
  const body = card.el.querySelector(".body");
  if (!body) return;
  const chrome = card.el.offsetHeight - body.clientHeight;   // 枠と余白のぶん
  const need = Math.ceil(body.scrollHeight + chrome);
  if (need > card.h + 1 || (shrink && need < card.h - 1)) {
    card.h = Math.max(225, need);
    layoutCard(card);
    markDirty();
  }
}

function onCardMove(e) {
  if (!cardDrag || cardDrag.id !== e.pointerId) return;
  const dx = (e.clientX - cardDrag.sx) / viewport.zoom;
  const dy = (e.clientY - cardDrag.sy) / viewport.zoom;
  if (!cardDrag.moved && Math.hypot(dx, dy) * viewport.zoom < 4) return;   // 手ぶれは無視
  cardDrag.moved = true;
  const c = cardDrag.card;
  if (cardDrag.mode === "move") {
    c.x = Math.round(cardDrag.ox + dx);
    c.y = Math.round(cardDrag.oy + dy);
  } else {
    if (cardDrag.axis !== "h") c.w = Math.max(300, Math.round(cardDrag.ow + dx));
    if (cardDrag.axis !== "w") c.h = Math.max(225, Math.round(cardDrag.oh + dy));
  }
  layoutCard(c);
}

function onCardUp(e) {
  if (!cardDrag || cardDrag.id !== e.pointerId) return;
  if (cardDrag.moved) {
    if (cardDrag.mode === "resize" && cardDrag.axis === "w") fitCardHeight(cardDrag.card);
    pushCardChange(cardDrag.card, cardDrag.geo0, cardState(cardDrag.card));
    markDirty();
  }
  // 動かしていない かつ すでに選ばれていた → 編集に入る（タップ2回で編集）
  if (!cardDrag.moved && cardDrag.mode === "move" && cardDrag.wasSelected) {
    beginEdit(cardDrag.card);
  }
  cardDrag = null;
}
document.addEventListener("pointermove", onCardMove, { passive: true });
document.addEventListener("pointerup", onCardUp);
document.addEventListener("pointercancel", onCardUp);

/* ============================================================
   canvas ― インクの3枚重ね（カードの上に重なる）
   ============================================================ */
const stage = $("stage");
const cDry = $("cDry"), cPred = $("cPred"), cWet = $("cWet");
let ctxDry, ctxPred, ctxWet, dpr = 1, cssW = 0, cssH = 0;

/* 低遅延キャンバス（desynchronized）は、実機で確かめた iPad / iPhone だけで使う。
 * Android の Chrome では、透明であるべき部分が真っ黒に塗られ、
 * 下のカードや方眼まで覆い隠してしまう（2026-10-01 Xiaomi タブレットで発生）。
 * Android のタブレットは「PC版サイト」の名乗りをすることがあるので、
 * 「Android かどうか」ではなく「Apple のタッチ端末かどうか」で決める。
 * 手動で切り替えたいときは URL の末尾に ?lowlatency=1 / ?lowlatency=0 を付ける。 */
const LOW_LATENCY = (() => {
  const q = new URLSearchParams(location.search).get("lowlatency");
  if (q === "1") return true;
  if (q === "0") return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
})();
const WET_OPTS = LOW_LATENCY ? { desynchronized: true } : {};

function setupContexts() {
  ctxDry  = cDry.getContext("2d");
  ctxWet  = cWet.getContext("2d", WET_OPTS);
  ctxPred = cPred.getContext("2d", WET_OPTS);
}
setupContexts();

function resize() {
  dpr = Math.min(window.devicePixelRatio || 1, 3);
  cssW = Math.max(1, stage.clientWidth);
  cssH = Math.max(1, stage.clientHeight);
  for (const cv of [cDry, cPred, cWet]) {
    cv.width = Math.round(cssW * dpr);
    cv.height = Math.round(cssH * dpr);
  }
  setupContexts();
  applyViewport();
}
new ResizeObserver(resize).observe(stage);
addEventListener("resize", resize);
addEventListener("orientationchange", () => setTimeout(resize, 120));
/** 画面の大きさの通知を取りこぼしても、描く直前に必ず整合させる */
function ensureSize() {
  // resize() 側と同じ丸め方で比べる。0 を 1 に切り上げる差で呼び合わないように
  const w = Math.max(1, stage.clientWidth), h = Math.max(1, stage.clientHeight);
  if (cssW !== w || cssH !== h) resize();
}

function applyTransform(ctx) {
  ctx.setTransform(dpr * viewport.zoom, 0, 0, dpr * viewport.zoom, dpr * viewport.x, dpr * viewport.y);
  ctx.lineCap = "round"; ctx.lineJoin = "round";
}
function clearAll(ctx) {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
}
const cssVar = (n) => getComputedStyle(document.documentElement).getPropertyValue(n).trim();
function penColor() { return color === "ink" ? cssVar("--ink") : color; }

function widthAt(buf, i, base) {
  const p = buf.p(i);
  return Math.max(0.35, base * (0.3 + 0.7 * (p > 0 ? p : 0.5)));
}
function drawSegment(ctx, buf, i, color, base) {
  if (i < 1 || i + 1 > buf.n - 1) return;
  const mx0 = (buf.x(i-1)+buf.x(i))/2, my0 = (buf.y(i-1)+buf.y(i))/2;
  const mx1 = (buf.x(i)+buf.x(i+1))/2, my1 = (buf.y(i)+buf.y(i+1))/2;
  ctx.strokeStyle = color;
  ctx.lineWidth = widthAt(buf, i, base);
  ctx.beginPath();
  ctx.moveTo(mx0, my0);
  ctx.quadraticCurveTo(buf.x(i), buf.y(i), mx1, my1);
  ctx.stroke();
}
function drawWholeStroke(ctx, s) {
  const b = s.buf;
  if (b.n === 1) {
    ctx.fillStyle = s.color;
    ctx.beginPath();
    ctx.arc(b.x(0), b.y(0), widthAt(b, 0, s.width)/2, 0, Math.PI*2);
    ctx.fill();
    return;
  }
  for (let i = 1; i < b.n - 1; i++) drawSegment(ctx, b, i, s.color, s.width);
}

function redrawInk() {
  clearAll(ctxDry);
  applyTransform(ctxDry);
  const tl = viewport.toWorld(0, 0), br = viewport.toWorld(cssW, cssH);
  for (const s of ink.strokes) {
    const b = s.bbox;
    if (b[2] < tl.x || b[0] > br.x || b[3] < tl.y || b[1] > br.y) continue;   // 画面外は描かない
    drawWholeStroke(ctxDry, s);
  }
}

/** ビューポートが動いたら、インクとカード層の両方に同じ変換をかける */
function applyViewport() {
  cardLayer.style.transform =
    `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`;
  cardLayer.style.setProperty("--iz", 1 / viewport.zoom);   // 枠線や取っ手を一定の太さに保つ
  const step = 40 * viewport.zoom;
  stage.style.backgroundSize = `${step}px ${step}px`;
  stage.style.backgroundPosition = `${viewport.x}px ${viewport.y}px`;
  ensureSize();
  $("zoomOut").textContent = Math.round(viewport.zoom * 100) + "%";
  redrawInk();
  if (marquee || lassoPath) drawMarquee();
  markDirty(true);
}

/* ============================================================
   投げ縄 ― 囲って選び、まとめて扱う
   ============================================================ */
let lassoPath = null;    // 描いている最中の囲い（世界座標）
let marquee = null;      // 選び終わったもの { strokes, cards, box }
let lassoDrag = null;    // 動かす／拡大縮小の最中の状態

const HANDLE_R = 13;     // 拡大縮小のつまみの半径（画面上の大きさ）

function pointInPoly(x, y, pts) {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x, yi = pts[i].y, xj = pts[j].x, yj = pts[j].y;
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** 囲いの中にあるものを拾う。線は「大半が中に入っていれば」選ぶ */
function collectInLasso(poly) {
  const picked = { strokes: [], cards: [] };
  for (const s of ink.strokes) {
    const step = Math.max(1, Math.floor(s.buf.n / 24));
    let inside = 0, total = 0;
    for (let i = 0; i < s.buf.n; i += step) {
      total++;
      if (pointInPoly(s.buf.x(i), s.buf.y(i), poly)) inside++;
    }
    if (total && inside / total >= 0.6) picked.strokes.push(s);
  }
  for (const c of cards) {
    if (pointInPoly(c.x + c.w / 2, c.y + c.h / 2, poly)) picked.cards.push(c);
  }
  return picked;
}

function marqueeBox(m) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const s of m.strokes) {
    x0 = Math.min(x0, s.bbox[0]); y0 = Math.min(y0, s.bbox[1]);
    x1 = Math.max(x1, s.bbox[2]); y1 = Math.max(y1, s.bbox[3]);
  }
  for (const c of m.cards) {
    x0 = Math.min(x0, c.x); y0 = Math.min(y0, c.y);
    x1 = Math.max(x1, c.x + c.w); y1 = Math.max(y1, c.y + c.h);
  }
  return Number.isFinite(x0) ? [x0, y0, x1, y1] : null;
}

function moveMarquee(m, dx, dy) {
  for (const s of m.strokes) {
    for (let i = 0; i < s.buf.n; i++) {
      s.buf.a[i * STRIDE] += dx;
      s.buf.a[i * STRIDE + 1] += dy;
    }
    recalcBBox(s);
  }
  for (const c of m.cards) { c.x = Math.round(c.x + dx); c.y = Math.round(c.y + dy); layoutCard(c); }
  m.box = marqueeBox(m);
  redrawInk();
  drawMarquee();
}

/** 左上を留め具にして拡大縮小する。線は太さも一緒に変える */
function scaleMarquee(m, k, ax, ay) {
  for (const s of m.strokes) {
    for (let i = 0; i < s.buf.n; i++) {
      s.buf.a[i * STRIDE]     = ax + (s.buf.a[i * STRIDE] - ax) * k;
      s.buf.a[i * STRIDE + 1] = ay + (s.buf.a[i * STRIDE + 1] - ay) * k;
    }
    s.width = Math.max(0.4, s.width * k);
    recalcBBox(s);
  }
  // カードは位置だけ動かす（中の文字の組み方が変わってしまうため、大きさは変えない）
  for (const c of m.cards) {
    c.x = Math.round(ax + (c.x - ax) * k);
    c.y = Math.round(ay + (c.y - ay) * k);
    layoutCard(c);
  }
  m.box = marqueeBox(m);
  redrawInk();
  drawMarquee();
}

function clearMarquee() {
  marquee = null;
  lassoPath = null;
  clearAll(ctxPred);
}

/** 囲いと、選んだものを囲む枠を描く */
function drawMarquee() {
  clearAll(ctxPred);
  const z = viewport.zoom;

  if (lassoPath && lassoPath.length > 1) {
    applyTransform(ctxPred);
    ctxPred.strokeStyle = cssVar("--accent");
    ctxPred.lineWidth = 1.5 / z;
    ctxPred.setLineDash([6 / z, 5 / z]);
    ctxPred.beginPath();
    ctxPred.moveTo(lassoPath[0].x, lassoPath[0].y);
    for (let i = 1; i < lassoPath.length; i++) ctxPred.lineTo(lassoPath[i].x, lassoPath[i].y);
    ctxPred.closePath();
    ctxPred.stroke();
    ctxPred.setLineDash([]);
  }

  if (marquee && marquee.box) {
    const [x0, y0, x1, y1] = marquee.box;
    applyTransform(ctxPred);
    ctxPred.strokeStyle = cssVar("--accent");
    ctxPred.lineWidth = 1.5 / z;
    ctxPred.setLineDash([7 / z, 5 / z]);
    ctxPred.strokeRect(x0, y0, x1 - x0, y1 - y0);
    ctxPred.setLineDash([]);
    // 右下のつまみ
    ctxPred.fillStyle = cssVar("--accent");
    ctxPred.beginPath();
    ctxPred.arc(x1, y1, HANDLE_R / z, 0, Math.PI * 2);
    ctxPred.fill();
    ctxPred.strokeStyle = cssVar("--card");
    ctxPred.lineWidth = 3 / z;
    ctxPred.stroke();
  }
}

const onHandle = (q) => {
  if (!marquee || !marquee.box) return false;
  const [, , x1, y1] = marquee.box;
  return Math.hypot(q.x - x1, q.y - y1) <= (HANDLE_R + 8) / viewport.zoom;
};
const inBox = (q) => {
  if (!marquee || !marquee.box) return false;
  const [x0, y0, x1, y1] = marquee.box;
  return q.x >= x0 && q.x <= x1 && q.y >= y0 && q.y <= y1;
};

/** 選んだものをまとめて消す */
function deleteMarquee() {
  if (!marquee) return;
  const removed = [];
  for (const s of marquee.strokes) {
    const i = ink.strokes.indexOf(s);
    if (i >= 0) { removed.push({ stroke: s, at: i }); ink.strokes.splice(i, 1); }
  }
  const cardSnaps = marquee.cards.map((c) => ({
    id: c.id, type: c.type, props: cloneProps(c.props), x: c.x, y: c.y, w: c.w, h: c.h
  }));
  for (const c of marquee.cards.slice()) removeCard(c);

  pushHistory({
    undo: () => {
      for (let i = removed.length - 1; i >= 0; i--) {
        ink.strokes.splice(Math.min(removed[i].at, ink.strokes.length), 0, removed[i].stroke);
      }
      for (const d of cardSnaps) restoreCard(d);
      redrawInk(); updateEmpty();
    },
    redo: () => {
      for (const r of removed) { const i = ink.strokes.indexOf(r.stroke); if (i >= 0) ink.strokes.splice(i, 1); }
      for (const d of cardSnaps) { const c = cards.find((x) => x.id === d.id); if (c) removeCard(c); }
      redrawInk(); updateEmpty();
    }
  });
  clearMarquee();
  redrawInk(); updateEmpty(); markDirty();
}

/* ============================================================
   tools / input
   ============================================================ */
let tool = "select";
let color = "ink";
let baseWidth = 3.5;
let active = null;
const touches = new Map();
let penActive = false, lastPenAt = 0, panning = null, pinch = null;
const PALM_GUARD_MS = 700, PEN_STUCK_MS = 1500;

function isPenEngaged() {
  if (!penActive) return false;
  if (performance.now() - lastPenAt > PEN_STUCK_MS) { penActive = false; return false; }
  return true;
}
function localXY(e) {
  const r = stage.getBoundingClientRect();
  return { px: e.clientX - r.left, py: e.clientY - r.top };
}
function toPoint(e) {
  const { px, py } = localXY(e);
  const w = viewport.toWorld(px, py);
  return { x: w.x, y: w.y, p: e.pressure, tx: e.tiltX || 0, ty: e.tiltY || 0, t: e.timeStamp };
}
function beginStroke(q, pointerId) {
  active = { buf: new StrokeBuf(), color: penColor(), token: color, width: baseWidth, pointerId };
  active.buf.push(q.x, q.y, q.p, q.tx, q.ty, performance.now());
  applyTransform(ctxWet);
}
function extendStroke(pts) {
  const b = active.buf;
  for (const q of pts) b.push(q.x, q.y, q.p, q.tx, q.ty, q.t);
  for (let i = Math.max(1, b.n - pts.length - 1); i < b.n - 1; i++) {
    drawSegment(ctxWet, b, i, active.color, active.width);
  }
}
function endStroke() {
  if (!active) return;
  const s = active;
  pushHistory({
    undo: () => { const i = ink.strokes.indexOf(s); if (i >= 0) ink.strokes.splice(i, 1); redrawInk(); updateEmpty(); },
    redo: () => { ink.strokes.push(s); redrawInk(); updateEmpty(); }
  });
  ink.add(active);
  applyTransform(ctxDry);
  drawWholeStroke(ctxDry, active);
  active = null;
  clearAll(ctxWet); clearAll(ctxPred);
  updateEmpty();
  markDirty();
}
function cancelStroke() { active = null; clearAll(ctxWet); clearAll(ctxPred); }

let eraseBatch = null;      // ひと続きの消しゴム操作でまとめて消したもの

function eraseAt(wx, wy) {
  const r = (baseWidth * 3 + 8) / viewport.zoom;
  let hit = false;
  for (let k = ink.strokes.length - 1; k >= 0; k--) {
    const s = ink.strokes[k], b = s.bbox;
    if (wx < b[0] || wx > b[2] || wy < b[1] || wy > b[3]) continue;
    for (let i = 0; i < s.buf.n; i++) {
      if (Math.hypot(s.buf.x(i)-wx, s.buf.y(i)-wy) < r) {
        ink.strokes.splice(k, 1);
        if (eraseBatch) eraseBatch.push({ stroke: s, at: k });
        hit = true;
        break;
      }
    }
  }
  if (hit) { redrawInk(); updateEmpty(); markDirty(); }
}

/** 消しゴムを離したときに、その一続きを1回分として履歴へ積む */
function commitErase() {
  if (!eraseBatch || !eraseBatch.length) { eraseBatch = null; return; }
  const removed = eraseBatch.slice();
  eraseBatch = null;
  pushHistory({
    undo: () => {
      for (let i = removed.length - 1; i >= 0; i--) {
        ink.strokes.splice(Math.min(removed[i].at, ink.strokes.length), 0, removed[i].stroke);
      }
      redrawInk(); updateEmpty();
    },
    redo: () => {
      for (const r of removed) {
        const i = ink.strokes.indexOf(r.stroke);
        if (i >= 0) ink.strokes.splice(i, 1);
      }
      redrawInk(); updateEmpty();
    }
  });
}

function canDraw(e) {
  if (tool !== "pen" && tool !== "eraser") return false;
  if (e.pointerType === "mouse") return e.buttons === 1;
  return true;                    // ペンも指もパッシブスタイラスも描ける
}

stage.addEventListener("pointerdown", (e) => {
  if (cardDrag) {
    // カードを掴んでいる最中に2本目の指 → 掴みを取り消して拡大縮小に切り替える
    if (e.pointerType === "touch") {
      touches.set(e.pointerId, localXY(e));
      if (touches.size === 2) {
        const [a, b] = [...touches.values()];
        pinch = { d: Math.hypot(a.px-b.px, a.py-b.py), cx: (a.px+b.px)/2, cy: (a.py+b.py)/2 };
        const c = cardDrag.card;
        if (cardDrag.mode === "move") { c.x = cardDrag.ox; c.y = cardDrag.oy; }
        else { c.w = cardDrag.ow; c.h = cardDrag.oh; }
        layoutCard(c);
        cardDrag = null;
      }
    }
    return;
  }
  try { stage.setPointerCapture(e.pointerId); } catch (err) {}
  if (e.pointerType === "pen") { penActive = true; lastPenAt = performance.now(); }

  // ペンを使っている間だけ指を捨てる（離しを取りこぼしても自動で復帰する）
  if (e.pointerType === "touch" && (isPenEngaged() || performance.now() - lastPenAt < PALM_GUARD_MS)) return;

  if (e.pointerType === "touch") {
    if (e.isPrimary) { touches.clear(); pinch = null; panning = null; }
    touches.set(e.pointerId, localXY(e));
    if (touches.size === 2) {
      const [a, b] = [...touches.values()];
      pinch = { d: Math.hypot(a.px-b.px, a.py-b.py), cx: (a.px+b.px)/2, cy: (a.py+b.py)/2 };
      panning = null;
      if (active) cancelStroke();
      return;
    }
    if (!e.isPrimary) return;     // 手のひら対策：最初の1点だけを使う
  }

  if (tool === "lasso" && (e.pointerType !== "mouse" || e.buttons === 1)) {
    const q = toPoint(e);
    if (onHandle(q)) {
      lassoDrag = { mode: "scale", id: e.pointerId, anchor: [marquee.box[0], marquee.box[1]],
                    start: q, w0: marquee.box[2] - marquee.box[0], k: 1 };
      return;
    }
    if (inBox(q)) {
      lassoDrag = { mode: "move", id: e.pointerId, last: q, dx: 0, dy: 0 };
      return;
    }
    clearMarquee();
    lassoPath = [{ x: q.x, y: q.y }];
    lassoDrag = { mode: "draw", id: e.pointerId };
    return;
  }

  if (canDraw(e)) {
    const q = toPoint(e);
    if (tool === "eraser") { eraseBatch = []; eraseAt(q.x, q.y); return; }
    beginStroke(q, e.pointerId);
    return;
  }

  // 何もない場所を触った → 選択を解除して移動
  if (tool === "select") { endEdit(); select(null); }
  const { px, py } = localXY(e);
  panning = { id: e.pointerId, px, py };
}, { passive: false });

stage.addEventListener("pointermove", (e) => {
  if (e.pointerType === "pen") lastPenAt = performance.now();
  if (e.pointerType === "touch" && touches.has(e.pointerId)) touches.set(e.pointerId, localXY(e));

  if (pinch && touches.size >= 2) {
    const [a, b] = [...touches.values()];
    const d = Math.hypot(a.px-b.px, a.py-b.py);
    const cx = (a.px+b.px)/2, cy = (a.py+b.py)/2;
    if (pinch.d > 0) viewport.zoomAt(cx, cy, d / pinch.d);
    viewport.x += cx - pinch.cx; viewport.y += cy - pinch.cy;
    pinch = { d, cx, cy };
    applyViewport();
    return;
  }
  if (panning && panning.id === e.pointerId) {
    const { px, py } = localXY(e);
    viewport.x += px - panning.px; viewport.y += py - panning.py;
    panning.px = px; panning.py = py;
    applyViewport();
    return;
  }
  if (lassoDrag && lassoDrag.id === e.pointerId) {
    const q = toPoint(e);
    if (lassoDrag.mode === "draw") {
      const last = lassoPath[lassoPath.length - 1];
      if (Math.hypot(q.x - last.x, q.y - last.y) * viewport.zoom > 2) {
        lassoPath.push({ x: q.x, y: q.y });
        drawMarquee();
      }
    } else if (lassoDrag.mode === "move") {
      const dx = q.x - lassoDrag.last.x, dy = q.y - lassoDrag.last.y;
      lassoDrag.dx += dx; lassoDrag.dy += dy;
      lassoDrag.last = q;
      moveMarquee(marquee, dx, dy);
    } else if (lassoDrag.mode === "scale") {
      const [ax, ay] = lassoDrag.anchor;
      const w = Math.max(8, q.x - ax);
      const target = Math.max(0.1, w / Math.max(8, lassoDrag.w0));
      const k = target / lassoDrag.k;
      if (Number.isFinite(k) && k > 0) { scaleMarquee(marquee, k, ax, ay); lassoDrag.k = target; }
    }
    return;
  }

  if (tool === "eraser" && e.buttons === 1) { const q = toPoint(e); eraseAt(q.x, q.y); return; }
  if (!active || active.pointerId !== e.pointerId) return;

  const raw = typeof e.getCoalescedEvents === "function" ? e.getCoalescedEvents() : [];
  extendStroke((raw.length ? raw : [e]).map(toPoint));

  clearAll(ctxPred);
  if (typeof e.getPredictedEvents === "function") {
    const pr = e.getPredictedEvents();
    if (pr.length) {
      applyTransform(ctxPred);
      ctxPred.globalAlpha = 0.5;
      const b = active.buf;
      const tail = new StrokeBuf(pr.length + 2);
      tail.push(b.x(b.n-1), b.y(b.n-1), b.p(b.n-1), b.tx(b.n-1), b.ty(b.n-1), 0);
      for (const pe of pr) { const q = toPoint(pe); tail.push(q.x, q.y, q.p, q.tx, q.ty, q.t); }
      for (let i = 1; i < tail.n - 1; i++) drawSegment(ctxPred, tail, i, active.color, active.width);
      ctxPred.globalAlpha = 1;
    }
  }
}, { passive: false });

function release(e) {
  if (e.pointerType === "touch") { touches.delete(e.pointerId); if (touches.size < 2) pinch = null; }
  if (e.pointerType === "pen") { penActive = false; lastPenAt = performance.now(); }
  if (panning && panning.id === e.pointerId) panning = null;
  if (lassoDrag && lassoDrag.id === e.pointerId) {
    const d = lassoDrag;
    lassoDrag = null;
    if (d.mode === "draw") {
      if (lassoPath && lassoPath.length > 2) {
        const picked = collectInLasso(lassoPath);
        lassoPath = null;
        if (picked.strokes.length || picked.cards.length) {
          picked.box = marqueeBox(picked);
          marquee = picked;
          toast(`${picked.strokes.length + picked.cards.length} 個を選びました`);
        } else {
          marquee = null;
          toast("囲いの中に何もありませんでした");
        }
      } else { lassoPath = null; marquee = null; }
      drawMarquee();
    } else if (d.mode === "move" && (d.dx || d.dy)) {
      const m = marquee, dx = d.dx, dy = d.dy;
      pushHistory({ undo: () => moveMarquee(m, -dx, -dy), redo: () => moveMarquee(m, dx, dy) });
      markDirty();
    } else if (d.mode === "scale" && d.k !== 1) {
      const m = marquee, k = d.k, [ax, ay] = d.anchor;
      pushHistory({ undo: () => scaleMarquee(m, 1 / k, ax, ay), redo: () => scaleMarquee(m, k, ax, ay) });
      markDirty();
    }
  }
  if (active && active.pointerId === e.pointerId) endStroke();
  if (eraseBatch) commitErase();
}
stage.addEventListener("pointerup", release);
stage.addEventListener("pointercancel", release);
stage.addEventListener("lostpointercapture", release);

stage.addEventListener("wheel", (e) => {
  e.preventDefault();
  const { px, py } = localXY(e);
  const mouseWheel = Math.abs(e.deltaY) > 40 && e.deltaX === 0 && !e.shiftKey;
  if (e.ctrlKey || mouseWheel) viewport.zoomAt(px, py, Math.exp(-e.deltaY * 0.0016));
  else { viewport.x -= e.deltaX; viewport.y -= e.deltaY; }
  applyViewport();
}, { passive: false });

for (const t of ["gesturestart","gesturechange","gestureend"]) {
  stage.addEventListener(t, (e) => e.preventDefault(), { passive: false });
}
stage.addEventListener("touchmove", (e) => {
  if (e.target === stage || e.target.tagName === "CANVAS") e.preventDefault();
}, { passive: false });
stage.addEventListener("contextmenu", (e) => e.preventDefault());

/* ============================================================
   画像の取り込み（選ぶ・落とす・貼る）
   ============================================================ */
async function addImageFile(file, at) {
  if (!file || !file.type.startsWith("image/")) return;
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const hash = await saveAsset(bytes, file.type);
    const url = await assetURL(hash, file.type);
    if (!url) { toast("画像を読み込めませんでした"); return; }

    const probe = new Image();
    probe.onload = () => {
      const def = registry.get("image").defaultSize();
      const card = createCard("image", { asset: hash, mime: file.type, name: file.name }, at);
      const max = 1350;
      const k = Math.min(max / probe.width, max / probe.height, 1);
      const cx = card.x + def.w / 2, cy = card.y + def.h / 2;
      card.w = Math.round(probe.width * k);
      card.h = Math.round(probe.height * k);
      card.x = Math.round(cx - card.w / 2);
      card.y = Math.round(cy - card.h / 2);
      layoutCard(card);
      markDirty();
    };
    probe.src = url;
  } catch (e) {
    console.error(e);
    toast("画像を読み込めませんでした");
  }
}
$("filePick").addEventListener("change", (e) => {
  addImageFile(e.target.files[0]);
  e.target.value = "";
});
stage.addEventListener("dragover", (e) => { e.preventDefault(); });
stage.addEventListener("drop", (e) => {
  e.preventDefault();
  const { px, py } = localXY(e);
  const at = viewport.toWorld(px, py);
  for (const f of e.dataTransfer.files) addImageFile(f, at);
});
document.addEventListener("paste", (e) => {
  for (const item of e.clipboardData.items) {
    if (item.type.startsWith("image/")) { addImageFile(item.getAsFile()); e.preventDefault(); return; }
  }
});

/* ============================================================
   UI
   ============================================================ */
const toolBtns = { select: $("tSelect"), pen: $("tPen"), lasso: $("tLasso"), eraser: $("tEraser"), pan: $("tPan") };
function setTool(name) {
  tool = name;
  for (const [n, b] of Object.entries(toolBtns)) b.setAttribute("aria-pressed", String(n === name));
  // 選択ツールのときだけカードを掴める。ペンのときはカードの上から描ける
  cardLayer.classList.toggle("pick", name === "select");
  $("penOpts").style.opacity = (name === "pen" || name === "eraser") ? "1" : ".45";
  stage.style.cursor = name === "pan" ? "grab" : name === "eraser" ? "cell" : name === "pen" ? "crosshair"
                     : name === "lasso" ? "crosshair" : "default";
  if (name !== "select") { endEdit(); select(null); }
  if (name !== "lasso") clearMarquee();
}
for (const [name, btn] of Object.entries(toolBtns)) btn.addEventListener("click", () => setTool(name));

for (const sw of document.querySelectorAll(".swatch")) {
  sw.style.background = sw.dataset.color === "ink" ? "var(--ink)" : sw.dataset.color;
  sw.addEventListener("click", () => {
    color = sw.dataset.color;
    for (const o of document.querySelectorAll(".swatch")) o.setAttribute("aria-pressed", String(o === sw));
    if (tool !== "pen") setTool("pen");
  });
}
$("size").addEventListener("input", (e) => {
  baseWidth = parseFloat(e.target.value);
  $("sizeOut").textContent = baseWidth.toFixed(1);
});

$("btnUndo").addEventListener("click", undo);
$("btnRedo").addEventListener("click", redo);
$("addText").addEventListener("click", () => { setTool("select"); createCard("text"); });
$("addTodo").addEventListener("click", () => { setTool("select"); createCard("todo"); });
$("addImage").addEventListener("click", () => { setTool("select"); $("filePick").click(); });

$("btnFit").addEventListener("click", () => {
  const boxes = [
    ...cards.map(c => [c.x, c.y, c.x + c.w, c.y + c.h]),
    ...ink.strokes.map(s => s.bbox)
  ];
  if (!boxes.length) { viewport.x = 0; viewport.y = 0; viewport.zoom = 1; applyViewport(); return; }
  let x0=Infinity,y0=Infinity,x1=-Infinity,y1=-Infinity;
  for (const b of boxes) { x0=Math.min(x0,b[0]); y0=Math.min(y0,b[1]); x1=Math.max(x1,b[2]); y1=Math.max(y1,b[3]); }
  const z = clamp(Math.min(cssW/(x1-x0+120), cssH/(y1-y0+120)), 0.1, 2);
  viewport.zoom = z;
  viewport.x = cssW/2 - (x0+x1)/2*z;
  viewport.y = cssH/2 - (y0+y1)/2*z;
  applyViewport();
});
$("btnReset").addEventListener("click", () => {
  const snapshot = serializeBoard();
  pushHistory({
    undo: () => openBoardData(snapshot),
    redo: () => { while (cards.length) removeCard(cards[0]); ink.strokes.length = 0; redrawInk(); updateEmpty(); }
  });
  while (cards.length) removeCard(cards[0]);
  ink.strokes.length = 0;
  viewport.x = 0; viewport.y = 0; viewport.zoom = 1;
  applyViewport(); updateEmpty();
});

document.addEventListener("keydown", (e) => {
  const inField = document.activeElement && document.activeElement.isContentEditable;
  if (inField) {
    if (e.key === "Escape") { endEdit(); }
    return;
  }
  if ((e.key === "Delete" || e.key === "Backspace") && marquee) { e.preventDefault(); deleteMarquee(); }
  else if ((e.key === "Delete" || e.key === "Backspace") && selected) { e.preventDefault(); deleteCard(selected); }
  if (e.key === "Escape" && marquee) { clearMarquee(); }
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? redo() : undo(); }
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") { e.preventDefault(); redo(); }
  if (e.key === "v" || e.key === "1") setTool("select");
  if (e.key === "p" || e.key === "2") setTool("pen");
  if (e.key === "l" || e.key === "3") setTool("lasso");
  if (e.key === "e" || e.key === "4") setTool("eraser");
  if (e.key === "h" || e.key === "5") setTool("pan");
});

let toastTimer = null;
function toast(msg) {
  const t = $("toast");
  t.textContent = msg; t.classList.add("on");
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => t.classList.remove("on"), 2200);
}

// テーマが変わったらインクの色を作り直す
const mq = window.matchMedia("(prefers-color-scheme: dark)");
const onTheme = () => {
  const c = cssVar("--ink");
  for (const s of ink.strokes) if ((s.token || "ink") === "ink") s.color = c;
  redrawInk();
};
mq.addEventListener ? mq.addEventListener("change", onTheme) : mq.addListener(onTheme);
new MutationObserver(onTheme).observe(document.documentElement, { attributes:true, attributeFilter:["data-theme"] });


/* ============================================================
   保存 ― この端末に自動保存（IndexedDB → localStorage → メモリ）
   ============================================================ */
const IDX = "boards", LAST = "lastBoard", KEY = (id) => "board:" + id;

/** いま使っているスペース名。同期の設定と同じ場所から読む。
 *  空なら、これまでどおりの置き場（＝いまのボードはそのまま見える）。 */
function currentSpace() {
  try { return (JSON.parse(localStorage.getItem("inkboard.sync")) || {}).space || ""; }
  catch (e) { return ""; }
}
const SPACE = currentSpace();
const DB_NAME = SPACE ? "inkboard_" + SPACE : "inkboard";
const LS_PREFIX = SPACE ? "inkboard:" + SPACE + ":" : "inkboard:";

function openDB() {
  return new Promise((res, rej) => {
    let req;
    try { req = indexedDB.open(DB_NAME, 1); } catch (e) { return rej(e); }
    req.onupgradeneeded = () => { req.result.createObjectStore("kv"); };
    req.onsuccess = () => res(req.result);
    req.onerror = () => rej(req.error);
    setTimeout(() => rej(new Error("timeout")), 1200);
  });
}
const storage = {
  kind: "memory", db: null, mem: new Map(),
  async init() {
    try { this.db = await openDB(); this.kind = "indexeddb"; return; } catch (e) {}
    try {
      localStorage.setItem("__probe", "1"); localStorage.removeItem("__probe");
      this.kind = "localstorage"; return;
    } catch (e) {}
    this.kind = "memory";
  },
  _tx(mode) { return this.db.transaction("kv", mode).objectStore("kv"); },
  get(k) {
    if (this.kind === "indexeddb") return new Promise((res) => {
      const r = this._tx("readonly").get(k);
      r.onsuccess = () => res(r.result === undefined ? null : r.result);
      r.onerror = () => res(null);
    });
    if (this.kind === "localstorage") {
      try { const v = localStorage.getItem(LS_PREFIX + k); return Promise.resolve(v ? JSON.parse(v) : null); }
      catch (e) { return Promise.resolve(null); }
    }
    return Promise.resolve(this.mem.has(k) ? this.mem.get(k) : null);
  },
  set(k, v) {
    if (this.kind === "indexeddb") return new Promise((res, rej) => {
      const r = this._tx("readwrite").put(v, k);
      r.onsuccess = () => res(true);
      r.onerror = () => rej(r.error);
    });
    if (this.kind === "localstorage") {
      try { localStorage.setItem(LS_PREFIX + k, JSON.stringify(v)); return Promise.resolve(true); }
      catch (e) { return Promise.reject(e); }
    }
    this.mem.set(k, v); return Promise.resolve(true);
  },
  del(k) {
    if (this.kind === "indexeddb") return new Promise((res) => {
      const r = this._tx("readwrite").delete(k);
      r.onsuccess = r.onerror = () => res(true);
    });
    if (this.kind === "localstorage") { localStorage.removeItem(LS_PREFIX + k); return Promise.resolve(true); }
    this.mem.delete(k); return Promise.resolve(true);
  }
};

/* ── ボードの中身を読み書きできる形にする ─────────────── */
const r1 = (v) => Math.round(v * 10) / 10;
const r2 = (v) => Math.round(v * 100) / 100;

function serializeBoard() {
  return {
    v: 1, id: current.id, name: current.name, updatedAt: Date.now(),
    viewport: { x: r1(viewport.x), y: r1(viewport.y), zoom: r2(viewport.zoom) },
    cards: cards.map(c => ({ id: c.id, type: c.type, x: c.x, y: c.y, w: c.w, h: c.h, props: c.props })),
    strokes: ink.strokes.map(s => {
      const pts = [];
      for (let i = 0; i < s.buf.n; i++) pts.push(r1(s.buf.x(i)), r1(s.buf.y(i)), r2(s.buf.p(i)));
      return { id: s.id, token: s.token || "ink", width: s.width, pts };
    })
  };
}

function restoreCard(d) {
  if (!registry.has(d.type)) return null;      // 知らない種類は黙って飛ばす（前方互換）
  const card = { id: d.id || uid(), type: d.type, props: d.props, x: d.x, y: d.y, w: d.w, h: d.h };
  cards.push(card);
  mountCard(card);
  return card;
}

function clearBoard() {
  for (const c of cards) c.el.remove();
  cards.length = 0;
  ink.strokes.length = 0;
  selected = null; editing = null;
  clearAll(ctxWet); clearAll(ctxPred);
}

function openBoardData(d) {
  const keepSelected = selected ? selected.id : null;
  if (typeof clearMarquee === "function") clearMarquee();
  loading = true;
  clearBoard();
  current = { id: d.id, name: d.name || "無題のボード" };
  $("boardName").value = current.name;
  for (const cd of (d.cards || [])) restoreCard(cd);
  for (const sd of (d.strokes || [])) {
    const n = Math.floor(sd.pts.length / 3);
    const buf = new StrokeBuf(Math.max(8, n));
    for (let i = 0; i < sd.pts.length; i += 3) buf.push(sd.pts[i], sd.pts[i+1], sd.pts[i+2], 0, 0, 0);
    const token = sd.token || "ink";
    ink.add({ id: sd.id, buf, token, color: token === "ink" ? cssVar("--ink") : token, width: sd.width });
  }
  if (d.viewport) { viewport.x = d.viewport.x; viewport.y = d.viewport.y; viewport.zoom = d.viewport.zoom; }
  applyViewport();
  updateEmpty();
  loading = false;
  // 同期で作り直されたときに、選んでいたカードが外れないように戻す
  if (keepSelected) {
    const c = cards.find((x) => x.id === keepSelected);
    if (c) { selected = c; c.el.classList.add("sel"); }
  }
  clearHistory();
  setSaveState(storage.kind === "memory" ? "保存できません" : "保存済み", storage.kind === "memory");
  fire("open", current);
}

/* ── 自動保存 ───────────────────────────────── */
let current = null, loading = false, saveTimer = null, saving = false;

/* ── 同期層への通知口 ─────────────────────────────
 * change: 手元で何か変わった / open: 別のボードを開いた
 * 何も差し込まれていなければ、これまでどおり単体で動く。
 */
const hooks = { change: null, open: null, fetchAsset: null, putAsset: null, removeBoard: null };
function fire(name, arg) { if (hooks[name]) { try { hooks[name](arg); } catch (e) { console.error(e); } } }

function setSaveState(text, warn) {
  const el = $("saveState");
  el.textContent = text;
  el.classList.toggle("warn", !!warn);
}

function markDirty(quiet) {
  if (loading || !current) return;
  fire("change");
  setSaveState("保存中…");
  clearTimeout(saveTimer);
  saveTimer = setTimeout(saveNow, quiet ? 1200 : 600);
}

async function saveNow() {
  if (!current || saving) return;
  saving = true;
  const data = serializeBoard();
  try {
    await storage.set(KEY(current.id), data);
    const idx = (await storage.get(IDX)) || [];
    const row = { id: current.id, name: current.name, updatedAt: data.updatedAt };
    const i = idx.findIndex(b => b.id === current.id);
    if (i >= 0) idx[i] = row; else idx.push(row);
    await storage.set(IDX, idx);
    await storage.set(LAST, current.id);
    setSaveState(storage.kind === "memory" ? "保存できません" : "保存済み", storage.kind === "memory");
  } catch (e) {
    setSaveState("保存できません", true);
    toast("保存できませんでした。容量が足りない可能性があります");
  }
  saving = false;
}

addEventListener("pagehide", () => { clearTimeout(saveTimer); saveNow(); });
document.addEventListener("visibilitychange", () => { if (document.hidden) { clearTimeout(saveTimer); saveNow(); } });

/* ── ボードの管理 ───────────────────────────── */
async function newBoard(name, data) {
  const id = uid();
  const d = data || { v: 1, id, name: name || "無題のボード", cards: [], strokes: [],
                      viewport: { x: 0, y: 0, zoom: 1 } };
  d.id = id; d.name = name || d.name || "無題のボード";
  openBoardData(d);
  await saveNow();
  renderBoardList();
  return id;
}

async function switchBoard(id) {
  clearTimeout(saveTimer);
  await saveNow();
  let d = await storage.get(KEY(id));
  if (!d) {
    // 一覧には載っているのに、この端末に中身が無い。
    // 他の端末で作られたボードなので、空の器を作って開き、同期が中身を流し込むのを待つ。
    const idx = (await storage.get(IDX)) || [];
    const row = idx.find((b) => b.id === id) || {};
    d = {
      v: 1, id, name: row.name || "無題のボード", updatedAt: row.updatedAt || Date.now(),
      cards: [], strokes: [], viewport: { x: 0, y: 0, zoom: 1 }
    };
    await storage.set(KEY(id), d);
    toast("他の端末のボードを開いています…");
  }
  openBoardData(d);
  await storage.set(LAST, id);
  $("sheet").classList.remove("on");
  pendingBoardList = false;
  renderBoardList();
}

async function deleteBoard(id) {
  const idx = ((await storage.get(IDX)) || []).filter(b => b.id !== id);
  await storage.set(IDX, idx);
  await storage.del(KEY(id));

  // 同期側の一覧からも消す。ここを忘れると、消した直後に戻ってくる
  if (hooks.removeBoard) { try { await hooks.removeBoard(id); } catch (e) { console.error(e); } }

  if (current && current.id === id) {
    if (idx.length) {
      const d = await storage.get(KEY(idx[0].id));
      if (d) openBoardData(d); else await newBoard("無題のボード");
    } else {
      await newBoard("無題のボード");
    }
  }
  renderBoardList(true);      // 一覧を開いたままでも、その場で反映する
  toast("ボードを削除しました");
}

/** 他の端末で消されたボードを、この端末の一覧からも取り除く */
async function forgetBoard(id) {
  const idx = (await storage.get(IDX)) || [];
  if (!idx.some((b) => b.id === id)) return;
  await storage.set(IDX, idx.filter((b) => b.id !== id));
  await storage.del(KEY(id));
  if (current && current.id === id) {
    const rest = idx.filter((b) => b.id !== id);
    if (rest.length) {
      const d = await storage.get(KEY(rest[0].id));
      if (d) openBoardData(d); else await newBoard("無題のボード");
    } else {
      await newBoard("無題のボード");
    }
  }
  renderBoardList(true);
}

$("boardName").addEventListener("change", async (e) => {
  if (!current) return;
  current.name = e.target.value.trim() || "無題のボード";
  e.target.value = current.name;
  fire("change");          // 名前の変更も他の端末へ伝える
  await saveNow();
  renderBoardList();
});

/* ── ボード一覧の画面 ────────────────────────── */
const fmtDate = (t) => {
  const d = new Date(t), n = new Date();
  const sameDay = d.toDateString() === n.toDateString();
  const pad = (v) => String(v).padStart(2, "0");
  return sameDay ? `${pad(d.getHours())}:${pad(d.getMinutes())}`
                 : `${d.getMonth()+1}/${d.getDate()}`;
};

let pendingBoardList = false;

async function renderBoardList(force) {
  const list = $("boardList");
  // 開いている最中に作り直すと、押しかけの行が消えてしまう（クリックが成立しない）。
  // 閉じたあとにまとめて反映する。ただし削除など、その場で反映すべきときは force で通す。
  if (!force && $("sheet").classList.contains("on") && list.children.length) {
    pendingBoardList = true;
    return;
  }
  const idx = ((await storage.get(IDX)) || []).slice().sort((a, b) => b.updatedAt - a.updatedAt);
  list.innerHTML = "";
  for (const b of idx) {
    const row = document.createElement("div");
    row.className = "brow" + (current && b.id === current.id ? " cur" : "");
    const nm = document.createElement("div");
    nm.className = "nm"; nm.textContent = b.name;
    const meta = document.createElement("span");
    meta.className = "meta"; meta.textContent = fmtDate(b.updatedAt);
    const rm = document.createElement("button");
    rm.className = "rm"; rm.textContent = "✕"; rm.title = "このボードを削除";
    rm.addEventListener("pointerup", (e) => e.stopPropagation());
    rm.addEventListener("click", (e) => {
      e.stopPropagation();
      if (confirm(`「${b.name}」を削除します。よろしいですか。`)) deleteBoard(b.id);
    });
    row.append(nm);
    if (current && b.id === current.id) {
      const tag = document.createElement("span");
      tag.className = "cur-tag"; tag.textContent = "表示中";
      row.append(tag);
    }
    row.append(meta, rm);
    // click は「押した要素」と「離した要素」が同じでないと出ない。
    // pointerup なら、途中で描画が挟まっても取りこぼしにくい。
    row.addEventListener("pointerup", (e) => {
      if (e.target.closest(".rm")) return;
      if (!current || b.id !== current.id) switchBoard(b.id);
      else closeSheet();
    });
    list.appendChild(row);
  }
  if (!idx.length) {
    const e = document.createElement("div");
    e.className = "sheetNote"; e.style.padding = "18px 15px";
    e.textContent = "まだボードがありません。";
    list.appendChild(e);
  }
}

function closeSheet() {
  $("sheet").classList.remove("on");
  if (pendingBoardList) { pendingBoardList = false; renderBoardList(); }
}
$("btnBoards").addEventListener("click", () => { $("boardList").innerHTML = ""; renderBoardList(); $("sheet").classList.add("on"); });
$("sheetClose").addEventListener("click", closeSheet);
// 一覧の操作をキャンバスへ伝えない。伝わると setPointerCapture に横取りされ、
// 行から指を離したときの反応が届かなくなる。
$("sheet").addEventListener("pointerdown", (e) => {
  e.stopPropagation();
  if (e.target === $("sheet")) closeSheet();
});
$("btnNewBoard").addEventListener("click", async () => {
  await newBoard("無題のボード");
  $("sheet").classList.remove("on");
  toast("新しいボードを作りました");
});

/* ── 書き出し・読み込み（端末をまたぐときの手段）────────── */
async function useCap(name) {
  try {
    if (typeof claude === "undefined" || !claude.use) return null;
    return await claude.use(name);
  } catch (e) { return null; }
}

$("btnExport").addEventListener("click", async () => {
  const board = serializeBoard();
  // 画像は別置きなので、書き出すファイルには実体も同梱する
  board.assets = {};
  for (const c of board.cards) {
    if (c.type !== "image" || !c.props.asset || board.assets[c.props.asset]) continue;
    const bytes = await readAssetLocal(c.props.asset);
    if (bytes) board.assets[c.props.asset] = b64enc(bytes);
  }
  const data = JSON.stringify(board);
  const filename = (current.name || "board").replace(/[\\/:*?"<>|]/g, "_") + ".inkboard.json";
  const dl = await useCap("downloads");
  if (dl) {
    try { await dl.save({ filename, data }); toast("書き出しました"); }
    catch (e) { toast("書き出しは取り消されました"); }
    return;
  }
  try {
    const url = URL.createObjectURL(new Blob([data], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast("書き出しました");
  } catch (e) { toast("この画面からは書き出せませんでした"); }
});

$("btnImport").addEventListener("click", () => $("jsonPick").click());
$("jsonPick").addEventListener("change", (e) => {
  const f = e.target.files[0];
  e.target.value = "";
  if (!f) return;
  const fr = new FileReader();
  fr.onload = async () => {
    try {
      const d = JSON.parse(fr.result);
      if (!d || !Array.isArray(d.cards)) throw new Error("形式が違います");
      // 同梱されている画像を置き場へ入れてから開く
      if (d.assets) {
        for (const [hash, b64] of Object.entries(d.assets)) {
          const mime = (d.cards.find((c) => c.props && c.props.asset === hash) || { props: {} }).props.mime;
          await saveAsset(b64dec(b64), mime || "image/png");
        }
        delete d.assets;
      }
      await newBoard((d.name || "読み込んだボード") + "（読込）", d);
      $("sheet").classList.remove("on");
      toast("読み込みました");
    } catch (err) { toast("読み込めませんでした：" + err.message); }
  };
  fr.readAsText(f);
});

/* ── 起動 ───────────────────────────────────── */
function storageNote() {
  const where = SPACE ? `スペース「${SPACE}」／` : "";
  if (storage.kind === "indexeddb")
    return where + "この端末に自動保存しています（IndexedDB）。他の端末へ移すときは「書き出す」でファイルにしてください。";
  if (storage.kind === "localstorage")
    return "この端末に自動保存しています（localStorage）。容量が小さいため、画像を多く貼ると保存できなくなることがあります。";
  return "⚠ この画面では保存できません。作ったものは「書き出す」でファイルに残してください。";
}

async function boot() {
  setSaveState("読み込み中…");
  await storage.init();
  $("storageNote").textContent = storageNote();

  const idx = (await storage.get(IDX)) || [];
  const last = await storage.get(LAST);
  const id = (last && idx.some(b => b.id === last)) ? last : (idx[0] && idx[0].id);

  // 読み込みを待つ間に描かれたものがあれば、それを消してはいけない
  const hasWork = cards.length > 0 || ink.strokes.length > 0;
  if (id && !hasWork) {
    const d = await storage.get(KEY(id));
    if (d) { openBoardData(d); renderBoardList(); return; }
  }
  current = { id: uid(), name: hasWork ? "描きかけのボード" : "無題のボード" };
  $("boardName").value = current.name;
  await saveNow();
  renderBoardList();
  fire("open", current);
  if (hasWork) toast("描きかけの内容を新しいボードとして残しました");
  else setTimeout(() => toast("カードを置いて、ペンで上から書いてみてください"), 700);
}

setTool("select");
resize();
boot();

/* ── 同期層から呼び出すための入口 ─────────────────────────
 * Step 4B-2 で sync.js がここを掴んで Yjs と繋ぎ込む。
 */
export {
  viewport, ink, cards, registry, registerCard,
  createCard, restoreCard, mountCard, layoutCard, removeCard,
  clearBoard, serializeBoard, openBoardData,
  redrawInk, applyViewport, markDirty, storage, toast, uid,
  newBoard, switchBoard, renderBoardList, saveNow, undo, redo, clearHistory,
  saveAsset, assetURL, readAssetLocal, forgetBoard
};

/** いま開いているスペース名（同期層と画面表示で使う） */
export const getSpace = () => SPACE;

/** いま開いているボード（id と name） */
export const getCurrent = () => current;
/** 同期層からの差し込み口を登録する */
export function setHooks(h) { Object.assign(hooks, h); }
/** 手元の操作中か（描いている・編集している最中は遠隔の反映を待たせる） */
export const isBusy = () => !!(active || editing || cardDrag || panning || pinch);
